import { colors, cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DragEndEvent, useDndMonitor, useDroppable } from '@dnd-kit/core';
import React, { CSSProperties, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useResizeObserver } from 'common/hooks';
import { useLocaleUtils } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { HeaderImageUpload } from 'modules/menu/items/components/Item/HeaderImageUpload';
import { ItemQuantity } from 'modules/menu/items/types';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { SelectionCategory } from 'modules/shared-menu/types';

import OfferActions from './OfferActions';
import OverridesOfferActions from './OverridesOfferActions';
import {
  Header,
  Left,
  ThumbnailContainer,
  NameAndDescription,
  NameAndTags,
  Name,
  StyledTags,
  Description,
  Right,
  StyledWideAreaCheckbox,
} from './styles';
import { useSelectionData } from '../../../../hooks/useSelectionData';
import { ITEM_MAX_IMAGE_COUNT, MENU_MOBILE_BREAKPOINT } from '../../constants';
import { useOverrideSettings } from '../../hooks/useSelectionOverrideSettings';
import { Offer } from '../../types';
import { getOfferPurchasabilityErrors } from '../../unpurchasabilityUtils';
import CollapsibleThumbnails from '../CollapsibleThumbnails/CollapsibleThumbnails';
import { useImagesHandler } from '../CollapsibleThumbnails/useImagesHandler';
import { canSharedVenueUseBulkActions } from '../Collections/BulkActions/utils';
import { transformImagesApiToFormValues } from '../OfferDetailsForm/transformers';
import { getMenuEditorDragEventData } from '../dnd/utils';

type OfferHeaderProps = {
  active?: boolean;
  category: SelectionCategory | null;
  className?: string;
  disabledSelection: boolean;
  disableAssetDrop?: boolean;
  hidden?: boolean;
  offer: Offer;
  onClick?: (event: React.MouseEvent, offerId: string) => void;
  onCopy?: (itemId: string, isSpecial?: boolean) => void;
  onDeselect: (offerId: string) => void;
  onSelect: (offerId: string) => void;
  selected: boolean;
  style?: CSSProperties;
  itemInventoryCount?: ItemQuantity;
};

type WeightBasedItemQuantityProps = {
  itemInventoryCount: ItemQuantity;
};

const Root = styled.div`
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  cursor: pointer;
`;

const PriceWrapper = styled.div`
  margin-inline-end: 12px;
  display: flex;
  color: ${cbSemanticCssVars.textPrimary};
`;

const Price = styled(T.Body3)``;

const OriginalPrice = styled(T.Body3)`
  text-decoration: line-through;
`;

const DiscountPrice = styled(T.Body3)`
  color: ${colors.strawberry100};
  margin-inline-end: 12px;
`;

const Quantity = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

export const WeightBasedItemAvailableQuantity = React.memo(({ itemInventoryCount }: WeightBasedItemQuantityProps) => {
  return itemInventoryCount.balance < 1000 ? (
    <>
      {itemInventoryCount.available}/
      <FormattedNumber value={itemInventoryCount.balance} style="unit" unit="gram" unitDisplay="narrow" />
    </>
  ) : (
    <>
      {itemInventoryCount.available / 1000}/
      <FormattedNumber value={itemInventoryCount.balance / 1000} style="unit" unit="kilogram" unitDisplay="narrow" />
    </>
  );
});

const OfferHeader = ({
  style,
  category,
  className,
  offer,
  active,
  hidden,
  onClick,
  onCopy,
  onSelect,
  onDeselect,
  selected,
  disabledSelection,
  disableAssetDrop,
  itemInventoryCount,
}: OfferHeaderProps) => {
  const { options, selection, updateSelectionData } = useSelectionData();
  const { offerAttrsEnabledForOverride } = useOverrideSettings();

  const updateMenuData = useCallback(
    (updatedOffer: Offer) => updateSelectionData({ actionType: 'updateOffer', payload: updatedOffer }),
    [updateSelectionData],
  );

  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const isSingleVenue = !!vlmeConfig?.isSingleVenue;
  const menuPermissions = useOfferingMenuPermissionsContext();

  const sharedVenueCanUseBulkActions = canSharedVenueUseBulkActions(offerAttrsEnabledForOverride);
  const isCheckboxDisabled = (isSharedVenue && !sharedVenueCanUseBulkActions) || !menuPermissions.contentEdit;
  const primaryLanguage = selection.primary_language;
  const name = offer.name.find(t => t.lang === primaryLanguage) || offer.name[0];
  const description = offer.description
    ? offer.description.find(t => t.lang === primaryLanguage) || offer.description[0]
    : '';

  const existingImageUrls = useMemo(() => {
    return transformImagesApiToFormValues(offer).map(image => image.imageUrl);
  }, [offer]);

  const purchasabilityErrors = useMemo(() => getOfferPurchasabilityErrors(options, offer), [options, offer]);

  const existingImages = useMemo(
    () =>
      offer.images && offer.images.length > 0
        ? offer.images
        : offer.image
          ? [{ url: offer.image ?? undefined, blurhash: offer.image_blurhash ?? undefined }]
          : [],
    [offer],
  );

  const { handleFileDrop, handleImageDelete, changingImages, updateImages } = useImagesHandler({
    images: existingImages,
    onUpdateMenuData: updateMenuData,
    menuId: selection.id,
    offer,
  });

  const isBinaryInventory = useMemo(
    () => !offer.inventory_mode || offer.inventory_mode === 'forced_out_of_stock',
    [offer.inventory_mode],
  );

  const { setNodeRef, isOver, node } = useDroppable({
    id: `${offer.id}-header`,
    data: {
      type: 'DND_OFFER_HEADER',
    },
    disabled: disableAssetDrop,
  });

  const { width } = useResizeObserver(node);

  /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  const onDragEnd = useCallback(
    async ({ active }: DragEndEvent) => {
      if (!isOver) {
        return;
      }

      const activeEvent = active && getMenuEditorDragEventData(active);
      if (activeEvent?.type === 'DND_IMAGE_GALLERY_ASSET') {
        if (existingImageUrls.length + 1 > ITEM_MAX_IMAGE_COUNT) {
          toast.error(
            <FormattedMessage
              id="image-upload.listHeaderUpload.errors.over-max-amount"
              values={{ count: ITEM_MAX_IMAGE_COUNT }}
            />,
          );
          return;
        }

        if (existingImageUrls.includes(activeEvent.imageData.url)) {
          // TODO: Finalise copy-texts
          toast('Looks like the image is already attached to the offer!');
          return;
        }

        const updatedImageUrls = [...existingImageUrls, activeEvent.imageData.url];
        const payload = {
          offer,
          imageUrls: updatedImageUrls,
        };
        toast.promise(
          updateImages(payload).then((updatedOffer: Offer) => updateMenuData(updatedOffer)),
          {
            // TODO: Finalise copy-texts
            loading: 'Attaching image to offer',
            success: 'Image attached to offer successfully!',
            error: 'Failed to attach the image to the offer. Please try again.',
          },
        );
      }
    },
    [existingImageUrls, isOver, offer, updateImages, updateMenuData],
  );

  useDndMonitor({
    onDragEnd,
  });

  return (
    <HeaderImageUpload
      existingImageUrls={existingImageUrls}
      item={offer}
      menuId={selection.id}
      onUpdateMenuData={updateMenuData}
      onDrop={files => handleFileDrop(files)}
      type="shared-menu"
      disableDropzone={isSharedVenue || (isSingleVenue && !menuPermissions.contentEdit)}
    >
      {({ imageHoverActive }) => {
        const isThumbnailsExpanded = imageHoverActive || isOver || changingImages.length > 0;

        return (
          <Root className={className} style={style} onClick={e => onClick?.(e, offer.id)} ref={setNodeRef}>
            <Header active={active} hidden={hidden} selected={selected}>
              <Left $hasAdditionalMargin={isCheckboxDisabled || (isSingleVenue && !menuPermissions.contentEdit)}>
                {isCheckboxDisabled ? null : (
                  <StyledWideAreaCheckbox
                    selectionStatus={!!selected ? 'selected' : 'not-selected'}
                    onToggle={() => {
                      if (selected) {
                        onDeselect(offer.id);
                      } else {
                        onSelect(offer.id);
                      }
                    }}
                    disabled={disabledSelection}
                  />
                )}

                <ThumbnailContainer>
                  <CollapsibleThumbnails
                    isExpanded={isThumbnailsExpanded}
                    changingImages={changingImages}
                    images={existingImages}
                    imagesAlt={name.value}
                    onDrop={(files, index) => handleFileDrop(files, index)}
                    onDelete={handleImageDelete}
                    canDelete={menuPermissions.contentEdit && !isSharedVenue}
                  />
                </ThumbnailContainer>
                <NameAndDescription isHidden={isThumbnailsExpanded}>
                  <NameAndTags>
                    <Name dir="auto" lang={name.lang}>
                      {name.value}
                    </Name>
                    <StyledTags
                      enabledConfig={offer.enabled}
                      soldOut={offer.inventory_mode === 'forced_out_of_stock'}
                      primaryLanguage={primaryLanguage}
                      purchasabilityErrors={purchasabilityErrors}
                      special={!!category?.is_wolt_special}
                      isTiny={isNarrowView}
                    />
                    {!isBinaryInventory && itemInventoryCount && (
                      <Quantity>
                        {!offer.sell_by_weight_config && (
                          <>
                            {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                            <FormattedMessage id="menu-item.available" />
                          </>
                        )}
                        {offer.sell_by_weight_config && offer.sell_by_weight_config.input_type === 'grams' && (
                          <>
                            <WeightBasedItemAvailableQuantity itemInventoryCount={itemInventoryCount} />{' '}
                            <FormattedMessage id="menu-item.available" />
                          </>
                        )}
                        {offer.sell_by_weight_config && offer.sell_by_weight_config?.input_type !== 'grams' && (
                          <>
                            {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                            <FormattedMessage id="menu-item.available" />
                          </>
                        )}
                      </Quantity>
                    )}
                    {isNarrowView && <OfferPrice offer={offer} />}
                  </NameAndTags>
                  {!isNarrowView && description && (
                    <Description dir="auto" lang={description.lang}>
                      {description.value}
                    </Description>
                  )}
                </NameAndDescription>
              </Left>
              <Right>
                {!isNarrowView && <OfferPrice style={{ marginInlineStart: 16 }} offer={offer} />}
                {isSharedVenue ? (
                  <OverridesOfferActions offer={offer} selectionId={selection.id} />
                ) : (
                  <OfferActions category={category} offer={offer} selectionId={selection.id} onCopy={onCopy} />
                )}
              </Right>
            </Header>
          </Root>
        );
      }}
    </HeaderImageUpload>
  );
};

type OfferPriceProps = {
  offer: Offer;
  style?: CSSProperties;
};

export const OfferPrice = React.memo(({ offer, style }: OfferPriceProps) => {
  const { getLocalizedUnit } = useLocaleUtils();
  const { currency } = useCountryConfigContext();

  const price = useMemo(() => offer.price, [offer]);
  const discounted = useMemo(() => offer.discounted, [offer]);

  return (
    <PriceWrapper style={style}>
      {!discounted ? (
        <Price>
          <MonetaryAmount amount={parseFloat(String(price))} currency={currency} />
          {!!offer.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
        </Price>
      ) : (
        <>
          <DiscountPrice>
            <MonetaryAmount amount={parseFloat(String(price))} currency={currency} />
            {!!offer.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </DiscountPrice>
          <OriginalPrice>
            <MonetaryAmount amount={parseFloat(String(discounted.original_price))} currency={currency} />
            {!!offer.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </OriginalPrice>
        </>
      )}
    </PriceWrapper>
  );
});

export default React.memo(OfferHeader);
